import React, { useState, useEffect, useCallback } from 'react';
import { Layout } from '../../components/Layout/Layout';
import { api } from '../../services/api';
import { ProductPicker, PickerProduct } from './ProductPicker';
import { ClientOption } from './ClientCombobox';
import styles from './Ventes.module.css';

const TVA = 1.20;
let _key = 0;
const newKey = () => ++_key;

// ── Types ────────────────────────────────────────────────────────────────────

interface VenteResume {
  id: number; type: string; numeroCommande: string | null; clientNom: string | null;
  clientId: number | null; dateVente: string | null; dateFin: string | null;
  nomEvenement: string | null; modePaiement: string; fraisPort: number;
  remiseGenerale: number; createdAt: string; nbLignes: number;
  totalTtc: number; totalHt: number; coutAchatHt: number;
  beneficeHt: number; rentabilite: number;
}

interface ClientData {
  id: number; nom: string; createdAt: string;
  nbAchats: number; dernierAchat: string | null;
}

interface ClientDetail {
  client: { id: number; nom: string; createdAt: string };
  ventes: {
    id: number; type: string; dateVente: string | null;
    modePaiement: string; nbLignes: number; totalTtc: number; beneficeHt: number; createdAt: string;
  }[];
}

interface LigneApi {
  id: number; productId: number | null; produitNom: string; quantite: number;
  prixAchatHt: number; prixAchatTtc: number; prixVenteHt: number;
  prixVenteTtc: number; remiseLigne: number;
}

type ProductOption = PickerProduct;

interface LigneForm {
  _key: number; productId: number | null; produitNom: string;
  quantite: string; prixAchatHt: string; prixAchatTtc: string;
  prixVenteHt: string; prixVenteTtc: string; remiseLigne: string;
}

interface FormState {
  type: 'physique' | 'online' | 'evenement';
  clientId: number | null;
  clientNom: string; numeroCommande: string; dateVente: string; dateFin: string;
  nomEvenement: string; modePaiement: string; fraisPort: string; remiseGenerale: string;
}

const EMPTY_FORM: FormState = {
  type: 'physique', clientId: null, clientNom: '', numeroCommande: '',
  dateVente: '', dateFin: '', nomEvenement: '',
  modePaiement: 'inconnu', fraisPort: '0', remiseGenerale: '0',
};
const emptyLigne = (): LigneForm => ({
  _key: newKey(), productId: null, produitNom: '', quantite: '1',
  prixAchatHt: '0', prixAchatTtc: '0', prixVenteHt: '0', prixVenteTtc: '0', remiseLigne: '0',
});

// ── Helpers ───────────────────────────────────────────────────────────────────

const n = (s: string) => parseFloat(s) || 0;
const fmt = (v: number) => new Intl.NumberFormat('fr-FR', { style: 'currency', currency: 'EUR' }).format(v);
const fmtPct = (v: number) => v.toFixed(1) + '%';
const fmtDate = (d: string | null) => d ? new Date(d).toLocaleDateString('fr-FR') : '—';
const typeLabel = (t: string) => t === 'physique' ? '🏪 Physique' : t === 'online' ? '🌐 En ligne' : '🎪 Événement';

const calcTotals = (lignes: LigneForm[], form: FormState) => {
  const remiseG = n(form.remiseGenerale) / 100;
  let sousTtc = 0, sousHt = 0, coutHt = 0, coutTtc = 0;
  for (const l of lignes) {
    const qty = n(l.quantite), rl = 1 - n(l.remiseLigne) / 100;
    sousTtc += n(l.prixVenteTtc) * qty * rl;
    sousHt  += n(l.prixVenteHt)  * qty * rl;
    coutHt  += n(l.prixAchatHt)  * qty;
    coutTtc += n(l.prixAchatTtc) * qty;
  }
  const totalTtc = sousTtc * (1 - remiseG) + n(form.fraisPort);
  const totalHt  = sousHt  * (1 - remiseG);
  const beneficeHt = totalHt - coutHt;
  const rentabilite = coutHt > 0 ? (beneficeHt / coutHt) * 100 : 0;
  const remiseMontant = sousTtc * remiseG;
  return { sousTtc, sousHt, coutHt, coutTtc, totalTtc, totalHt, beneficeHt, rentabilite, remiseMontant };
};

// ── Component ─────────────────────────────────────────────────────────────────

const randomPassword = () => {
  const chars = 'ABCDEFGHJKMNPQRSTUVWXYZabcdefghjkmnpqrstuvwxyz23456789!@#$';
  return Array.from({ length: 12 }, () => chars[Math.floor(Math.random() * chars.length)]).join('');
};

export const Ventes: React.FC = () => {
  const [ventes, setVentes]     = useState<VenteResume[]>([]);
  const [loading, setLoading]   = useState(true);
  const [view, setView]         = useState<'list' | 'form' | 'clients' | 'clientDetail'>('list');
  const [editingId, setEditingId] = useState<number | null>(null);
  const [form, setForm]         = useState<FormState>(EMPTY_FORM);
  const [lignes, setLignes]     = useState<LigneForm[]>([emptyLigne()]);
  const [products, setProducts] = useState<ProductOption[]>([]);
  const [saving, setSaving]         = useState(false);
  const [filterType, setFilterType] = useState('');
  const [pickerKey, setPickerKey]       = useState<number | null>(null);
  const [knownClients, setKnownClients] = useState<ClientOption[]>([]);
  const [clientsData, setClientsData]   = useState<ClientData[]>([]);
  const [clientDetail, setClientDetail] = useState<ClientDetail | null>(null);
  const [loadingClient, setLoadingClient] = useState(false);
  const [showNewUser, setShowNewUser]   = useState(false);
  const [newUser, setNewUser]           = useState({ firstName: '', lastName: '', email: '' });
  const [creatingUser, setCreatingUser] = useState(false);
  const [dirtyLines, setDirtyLines]     = useState<Set<number>>(new Set());

  const load = useCallback(async () => {
    try { setLoading(true); const r = await api.get<VenteResume[]>('ventes'); setVentes(r.data ?? []); }
    catch { } finally { setLoading(false); }
  }, []);

  useEffect(() => { load(); }, [load]);

  useEffect(() => {
    // Combobox : tous les users (sauf Admin) depuis la table users
    if (view === 'form' && knownClients.length === 0) {
      api.get<any[]>('users').then(r => {
        const users = r.data ?? [];
        setKnownClients(
          users
            .filter((u: any) => u.role !== 'Admin')
            .map((u: any) => ({ id: u.id, nom: `${u.firstName ?? ''} ${u.lastName ?? ''}`.trim() }))
        );
      }).catch(() => {});
    }
    // Onglet clients : users qui ont au moins une vente
    if (view === 'clients') {
      api.get<ClientData[]>('ventes/clients').then(r => {
        setClientsData(r.data ?? []);
      }).catch(() => {});
    }
  }, [view]);

  useEffect(() => {
    if (view === 'form' && products.length === 0) {
      api.get<any>('products?limit=500&page=1').then(r => {
        const items = r.data?.data?.products ?? r.data?.items ?? (Array.isArray(r.data) ? r.data : []);
        setProducts(Array.isArray(items) ? items.map((p: any) => ({
          ...p,
          variants: (p.Variants ?? p.variants ?? []).map((v: any) => ({
            id: v.id ?? v.Id,
            name: v.name ?? v.Name ?? '',
            price: v.price ?? v.Price ?? 0,
            stock: v.stock ?? v.Stock ?? 0,
            option1Value: v.option1Value ?? v.Option1Value ?? '',
            option2Value: v.option2Value ?? v.Option2Value ?? '',
            option3Value: v.option3Value ?? v.Option3Value ?? '',
          })),
        })) : []);
      }).catch(() => {});
    }
  }, [view, products.length]);

  const reloadClients = async () => {
    const r = await api.get<any[]>('users');
    return (r.data ?? [])
      .filter((u: any) => u.role !== 'Admin')
      .map((u: any) => ({ id: u.id, nom: `${u.firstName ?? ''} ${u.lastName ?? ''}`.trim() }));
  };

  const handleCreateUser = async () => {
    const firstName = newUser.firstName.trim();
    if (!firstName) { alert('Le prénom est obligatoire.'); return; }
    const lastName = firstName;
    const email = `vente.${firstName.toLowerCase()}@eliquidesud.vente.com`;
    try {
      setCreatingUser(true);
      const r = await api.post<{ success: boolean; user: { id: number; firstName: string; lastName: string } }>('users/register', {
        firstName, lastName, email,
        password: randomPassword(), birthDate: '2000-01-01', role: 'User', company: '',
      });
      const created = r.data?.user;
      const updated = await reloadClients();
      setKnownClients(updated);
      if (created?.id) setForm(f => ({ ...f, clientId: created.id, clientNom: `${created.firstName} ${created.lastName}`.trim() }));
      setShowNewUser(false);
      setNewUser({ firstName: '', lastName: '', email: '' });
    } catch (err: any) {
      alert('Erreur création client : ' + (err.response?.data?.message || err.message));
    } finally {
      setCreatingUser(false);
    }
  };

  const openCreate = () => {
    setEditingId(null); setForm(EMPTY_FORM); setLignes([emptyLigne()]);
    setShowNewUser(false); setDirtyLines(new Set()); setView('form');
  };

  const openEdit = async (id: number) => {
    try {
      const r = await api.get<{ vente: any; lignes: LigneApi[] }>(`ventes/${id}`);
      const v = r.data.vente;
      setForm({
        type: v.type, clientId: v.clientId ?? null, clientNom: v.clientNom ?? '',
        numeroCommande: v.numeroCommande ?? '',
        dateVente: v.dateVente ? v.dateVente.split('T')[0] : '',
        dateFin: v.dateFin ? v.dateFin.split('T')[0] : '',
        nomEvenement: v.nomEvenement ?? '',
        modePaiement: v.modePaiement, fraisPort: String(v.fraisPort), remiseGenerale: String(v.remiseGenerale),
      });
      setLignes(r.data.lignes.map(l => ({
        _key: newKey(), productId: l.productId, produitNom: l.produitNom,
        quantite: String(l.quantite), prixAchatHt: String(l.prixAchatHt), prixAchatTtc: String(l.prixAchatTtc),
        prixVenteHt: String(l.prixVenteHt), prixVenteTtc: String(l.prixVenteTtc), remiseLigne: String(l.remiseLigne),
      })));
      setEditingId(id); setDirtyLines(new Set()); setView('form');
    } catch { alert('Erreur chargement de la vente.'); }
  };

  const handleSave = async () => {
    if (lignes.length === 0) { alert('Ajoutez au moins un produit.'); return; }
    if (lignes.some(l => !l.produitNom.trim())) { alert('Chaque ligne doit avoir un nom de produit.'); return; }
    try {
      setSaving(true);
      const payload = {
        ...form, clientId: form.clientId, fraisPort: n(form.fraisPort), remiseGenerale: n(form.remiseGenerale),
        dateFin: form.dateFin || null, nomEvenement: form.nomEvenement || null,
        lignes: lignes.map(l => ({
          productId: l.productId, produitNom: l.produitNom, quantite: Math.max(1, n(l.quantite)),
          prixAchatHt: n(l.prixAchatHt), prixAchatTtc: n(l.prixAchatTtc),
          prixVenteHt: n(l.prixVenteHt), prixVenteTtc: n(l.prixVenteTtc),
          remiseLigne: n(l.remiseLigne),
        })),
      };
      if (editingId) await api.put(`ventes/${editingId}`, payload);
      else await api.post('ventes', payload);
      // Recharge les users pour que le nouveau client apparaisse
      const usersRes = await api.get<any[]>('users');
      setKnownClients(
        (usersRes.data ?? [])
          .filter((u: any) => u.role !== 'Admin')
          .map((u: any) => ({ id: u.id, nom: `${u.firstName ?? ''} ${u.lastName ?? ''}`.trim() }))
      );
      await load(); setView('list');
    } catch (err: any) {
      alert('Erreur : ' + (err.response?.data?.error || err.message));
    } finally { setSaving(false); }
  };

  const openClientDetail = async (clientId: number) => {
    try {
      setLoadingClient(true);
      setClientDetail(null);
      const r = await api.get<ClientDetail>(`ventes/clients/${clientId}`);
      setClientDetail(r.data);
      setView('clientDetail' );
    } catch { alert('Erreur chargement client.'); }
    finally { setLoadingClient(false); }
  };

  const handleDelete = async (id: number) => {
    if (!window.confirm('Supprimer cette vente ?')) return;
    await api.delete(`ventes/${id}`); setVentes(p => p.filter(v => v.id !== id));
  };

  const addLigne = () => setLignes(p => [...p, emptyLigne()]);
  const removeLigne = (key: number) => {
    setLignes(p => p.filter(l => l._key !== key));
    setDirtyLines(prev => { const s = new Set(prev); s.delete(key); return s; });
  };
  const updateLigne = (key: number, patch: Partial<LigneForm>) => {
    const priceFields = ['prixAchatHt', 'prixAchatTtc', 'prixVenteHt', 'prixVenteTtc'];
    const touchesPrice = Object.keys(patch).some(k => priceFields.includes(k));
    setLignes(p => p.map(l => {
      if (l._key !== key) return l;
      if (l.productId && touchesPrice) setDirtyLines(prev => new Set([...prev, key]));
      return { ...l, ...patch };
    }));
  };

  const selectProduct = (key: number, prod: ProductOption) => {
    const pvTtc = prod.discountPrice ?? prod.price;
    const pvHt  = Math.round(pvTtc / TVA * 100) / 100;
    const paHt  = prod.prixAchat ?? 0;
    const paTtc = Math.round(paHt * TVA * 100) / 100;
    setLignes(p => p.map(l => l._key === key ? {
      ...l, productId: prod.id, produitNom: prod.name,
      prixVenteTtc: String(pvTtc), prixVenteHt: String(pvHt),
      prixAchatHt: String(paHt), prixAchatTtc: String(paTtc),
    } : l));
    setDirtyLines(prev => { const s = new Set(prev); s.delete(key); return s; });
  };

  const handleUpdateProduct = async (l: LigneForm) => {
    if (!l.productId) return;
    try {
      const r = await api.get<any>(`products/${l.productId}`);
      const current = r.data?.data ?? r.data;
      await api.put(`products/${l.productId}`, {
        ...current,
        prixAchat: n(l.prixAchatHt),
        price: n(l.prixVenteTtc),
      });
      setDirtyLines(prev => { const s = new Set(prev); s.delete(l._key); return s; });
      setProducts(prev => prev.map(p => p.id === l.productId
        ? { ...p, prixAchat: n(l.prixAchatHt), price: n(l.prixVenteTtc) }
        : p
      ));
    } catch (err: any) {
      alert('Erreur mise à jour produit : ' + (err.response?.data?.message || err.message));
    }
  };

  // ── Stats globales ────────────────────────────────────────────────────────
  const filtered = ventes.filter(v => !filterType || v.type === filterType);
  const stats = filtered.reduce((s, v) => ({
    ca: s.ca + v.totalTtc, benefice: s.benefice + v.beneficeHt, cout: s.cout + v.coutAchatHt,
  }), { ca: 0, benefice: 0, cout: 0 });
  const statsRenta = stats.cout > 0 ? (stats.benefice / stats.cout) * 100 : 0;

  const totals = calcTotals(lignes, form);

  // ── List view ─────────────────────────────────────────────────────────────
  // ── Vue clients ───────────────────────────────────────────────────────────
  if ((view as string) === 'clientDetail' && clientDetail) return (
    <Layout>
      <div className={styles.container}>
        <div className={styles.pageHeader}>
          <button className={styles.btnBack} onClick={() => setView('clients' )}>← Clients</button>
          <h1 className={styles.title}>👤 {clientDetail.client.nom}</h1>
          <span className={styles.muted}>Client depuis le {fmtDate(clientDetail.client.createdAt)}</span>
        </div>
        <div className={styles.statsRow}>
          <div className={styles.statCard}><div className={styles.statVal}>{clientDetail.ventes.length}</div><div className={styles.statLabel}>Achats</div></div>
          <div className={styles.statCard}><div className={styles.statVal}>{fmt(clientDetail.ventes.reduce((s,v)=>s+v.totalTtc,0))}</div><div className={styles.statLabel}>CA total TTC</div></div>
          <div className={styles.statCard}><div className={styles.statVal}>{fmt(clientDetail.ventes.reduce((s,v)=>s+v.beneficeHt,0))}</div><div className={styles.statLabel}>Bénéfice HT</div></div>
          <div className={styles.statCard}><div className={styles.statVal}>{clientDetail.ventes.length > 0 ? fmt(clientDetail.ventes.reduce((s,v)=>s+v.totalTtc,0)/clientDetail.ventes.length) : '—'}</div><div className={styles.statLabel}>Panier moyen</div></div>
        </div>
        <div className={styles.tableContainer}>
          <table className={styles.table}>
            <thead><tr><th>#</th><th>Type</th><th>Date</th><th>Produits</th><th>Total TTC</th><th>Bénéfice HT</th><th>Paiement</th><th>Actions</th></tr></thead>
            <tbody>
              {clientDetail.ventes.map(v => (
                <tr key={v.id} className={styles.row}>
                  <td className={styles.colId}>#{v.id}</td>
                  <td><span className={styles.typeBadge}>{typeLabel(v.type)}</span></td>
                  <td className={styles.muted}>{fmtDate(v.dateVente)}</td>
                  <td className={styles.center}>{v.nbLignes}</td>
                  <td className={styles.bold}>{fmt(v.totalTtc)}</td>
                  <td className={v.beneficeHt >= 0 ? styles.green : styles.red}>{fmt(v.beneficeHt)}</td>
                  <td className={styles.muted}>{v.modePaiement}</td>
                  <td><button className={styles.btnEdit} onClick={() => openEdit(v.id)}>✏️ Voir</button></td>
                </tr>
              ))}
              {clientDetail.ventes.length === 0 && <tr><td colSpan={8} className={styles.noData}>Aucun achat enregistré.</td></tr>}
            </tbody>
          </table>
        </div>
      </div>
    </Layout>
  );

  if ((view as string) === 'clients') return (
    <Layout>
      <div className={styles.container}>
        <div className={styles.pageHeader}>
          <h1 className={styles.title}>Clients</h1>
          <button className={styles.btnBack} onClick={() => setView('list')}>← Ventes</button>
        </div>
        <div className={styles.tableContainer}>
          <table className={styles.table}>
            <thead><tr><th>#</th><th>Nom</th><th>Client depuis</th><th>Nb achats</th><th>Dernier achat</th><th></th></tr></thead>
            <tbody>
              {clientsData.map(c => (
                <tr key={c.id} className={styles.row}>
                  <td className={styles.colId}>#{c.id}</td>
                  <td className={styles.bold}>👤 {c.nom}</td>
                  <td className={styles.muted}>{fmtDate(c.createdAt)}</td>
                  <td className={styles.center}>{c.nbAchats}</td>
                  <td className={styles.muted}>{fmtDate(c.dernierAchat)}</td>
                  <td>
                    <button className={styles.btnEdit} onClick={() => openClientDetail(c.id)} disabled={loadingClient}>
                      {loadingClient ? '...' : '📋 Historique'}
                    </button>
                  </td>
                </tr>
              ))}
              {clientsData.length === 0 && <tr><td colSpan={6} className={styles.noData}>Aucun client enregistré pour l'instant.</td></tr>}
            </tbody>
          </table>
        </div>
      </div>
    </Layout>
  );

  if (view === 'list') return (
    <Layout>
      <div className={styles.container}>
        <div className={styles.pageHeader}>
          <h1 className={styles.title}>Ventes</h1>
          <div style={{ display: 'flex', gap: '.6rem' }}>
            <button className={styles.btnBack} onClick={() => setView('clients' )}>👤 Clients</button>
            <button className={styles.btnAdd} onClick={openCreate}>+ Nouvelle vente</button>
          </div>
        </div>

        {/* Stats */}
        <div className={styles.statsRow}>
          <div className={styles.statCard}><div className={styles.statVal}>{filtered.length}</div><div className={styles.statLabel}>Ventes</div></div>
          <div className={styles.statCard}><div className={styles.statVal}>{fmt(stats.ca)}</div><div className={styles.statLabel}>CA TTC</div></div>
          <div className={styles.statCard}><div className={styles.statVal}>{fmt(stats.benefice)}</div><div className={styles.statLabel}>Bénéfice HT</div></div>
          <div className={`${styles.statCard} ${statsRenta >= 0 ? styles.statGreen : styles.statRed}`}>
            <div className={styles.statVal}>{fmtPct(statsRenta)}</div><div className={styles.statLabel}>Rentabilité</div>
          </div>
        </div>

        {/* Filtres */}
        <div className={styles.filterRow}>
          {['', 'physique', 'online', 'evenement'].map(t => (
            <button key={t} className={`${styles.filterBtn} ${filterType === t ? styles.filterBtnActive : ''}`}
              onClick={() => setFilterType(t)}>
              {t === '' ? 'Toutes' : typeLabel(t)}
            </button>
          ))}
        </div>

        {/* Tableau */}
        <div className={styles.tableContainer}>
          <table className={styles.table}>
            <thead><tr>
              <th>#</th><th>Type</th><th>Client / Événement</th><th>Date</th>
              <th>Produits</th><th>CA TTC</th><th>Bénéfice HT</th><th>Rentabilité</th><th>Paiement</th><th>Actions</th>
            </tr></thead>
            <tbody>
              {filtered.map(v => (
                <tr key={v.id} className={styles.row}>
                  <td className={styles.colId}>#{v.id}</td>
                  <td><span className={styles.typeBadge}>{typeLabel(v.type)}</span></td>
                  <td>
                    {v.type === 'evenement'
                      ? <span>{v.nomEvenement ?? <em className={styles.muted}>Événement</em>}</span>
                      : <span>{v.clientNom ?? '—'}</span>}
                    {v.numeroCommande && <div className={styles.muted}>#{v.numeroCommande}</div>}
                  </td>
                  <td className={styles.muted}>{fmtDate(v.dateVente)}</td>
                  <td className={styles.center}>{v.nbLignes}</td>
                  <td className={styles.bold}>{fmt(v.totalTtc)}</td>
                  <td className={v.beneficeHt >= 0 ? styles.green : styles.red}>{fmt(v.beneficeHt)}</td>
                  <td className={v.rentabilite >= 0 ? styles.green : styles.red}>{fmtPct(v.rentabilite)}</td>
                  <td className={styles.muted}>{v.modePaiement}</td>
                  <td>
                    <div className={styles.actions}>
                      <button className={styles.btnEdit} onClick={() => openEdit(v.id)}>✏️ Modifier</button>
                      <button className={styles.btnDel} onClick={() => handleDelete(v.id)}>🗑</button>
                    </div>
                  </td>
                </tr>
              ))}
              {!loading && filtered.length === 0 && (
                <tr><td colSpan={10} className={styles.noData}>Aucune vente. Cliquez sur "Nouvelle vente".</td></tr>
              )}
            </tbody>
          </table>
        </div>
      </div>
    </Layout>
  );

  // ── Form view ─────────────────────────────────────────────────────────────
  return (
    <Layout>
      <div className={styles.container}>
        <div className={styles.pageHeader}>
          <button className={styles.btnBack} onClick={() => setView('list')}>← Retour</button>
          <h1 className={styles.title}>{editingId ? `Modifier la vente #${editingId}` : 'Nouvelle vente'}</h1>
        </div>

        {/* Type */}
        <div className={styles.card}>
          <div className={styles.typeRow}>
            {(['physique', 'online', 'evenement'] as const).map(t => (
              <button key={t} className={`${styles.typeBtn} ${form.type === t ? styles.typeBtnActive : ''}`}
                onClick={() => setForm(f => ({ ...f, type: t }))}>
                {typeLabel(t)}
              </button>
            ))}
          </div>

          <div className={styles.formGrid}>
            {form.type !== 'evenement' && (
              <div style={{ gridColumn: '1 / -1' }}>
                <div className={styles.field}>
                  <label className={styles.label}>Client (optionnel)</label>
                  <select
                    className={styles.input}
                    value={form.clientId ?? ''}
                    onChange={e => {
                      const id = e.target.value ? Number(e.target.value) : null;
                      const client = id ? knownClients.find(c => c.id === id) : null;
                      setForm(f => ({ ...f, clientId: id, clientNom: client?.nom ?? '' }));
                    }}
                  >
                    <option value="">— Aucun client —</option>
                    {knownClients.map(c => (
                      <option key={c.id} value={c.id}>{c.nom}</option>
                    ))}
                  </select>
                </div>
                <button
                  type="button"
                  className={styles.btnNewUser}
                  onClick={() => setShowNewUser(v => !v)}
                >
                  {showNewUser ? '✕ Annuler' : '+ Créer un nouveau client'}
                </button>
                {showNewUser && (
                  <div className={styles.newUserBox}>
                    <div className={styles.field} style={{ maxWidth: 260 }}>
                      <label className={styles.label}>Prénom *</label>
                      <input
                        className={styles.input}
                        value={newUser.firstName}
                        onChange={e => setNewUser(u => ({ ...u, firstName: e.target.value }))}
                        placeholder="Jean"
                      />
                    </div>
                    <div className={styles.newUserNote}>
                      Nom = Prénom · Email : vente.{newUser.firstName || 'prénom'}@eliquidesud.vente.com · Mot de passe généré · Naissance : 01/01/2000
                    </div>
                    <button type="button" className={styles.btnCreateUser} onClick={handleCreateUser} disabled={creatingUser}>
                      {creatingUser ? 'Création...' : 'Créer le client'}
                    </button>
                  </div>
                )}
              </div>
            )}
            {form.type === 'online' && (
              <div className={styles.field}>
                <label className={styles.label}>N° de commande</label>
                <input className={styles.input} value={form.numeroCommande}
                  onChange={e => setForm(f => ({ ...f, numeroCommande: e.target.value }))}
                  placeholder="CMD-2025-001" />
              </div>
            )}
            {form.type === 'evenement' ? (
              <>
                <div className={styles.field}>
                  <label className={styles.label}>Nom de l'événement</label>
                  <input className={styles.input} value={form.nomEvenement}
                    onChange={e => setForm(f => ({ ...f, nomEvenement: e.target.value }))}
                    placeholder="Marché de Noël, Festival..." />
                </div>
                <div className={styles.field}>
                  <label className={styles.label}>Date de début</label>
                  <input className={styles.input} type="date" value={form.dateVente}
                    onChange={e => setForm(f => ({ ...f, dateVente: e.target.value }))} />
                </div>
                <div className={styles.field}>
                  <label className={styles.label}>Date de fin</label>
                  <input className={styles.input} type="date" value={form.dateFin}
                    onChange={e => setForm(f => ({ ...f, dateFin: e.target.value }))} />
                </div>
              </>
            ) : (
              <div className={styles.field}>
                <label className={styles.label}>Date (optionnel)</label>
                <input className={styles.input} type="date" value={form.dateVente}
                  onChange={e => setForm(f => ({ ...f, dateVente: e.target.value }))} />
              </div>
            )}
            <div className={styles.field}>
              <label className={styles.label}>Mode de paiement</label>
              <select className={styles.input} value={form.modePaiement}
                onChange={e => setForm(f => ({ ...f, modePaiement: e.target.value }))}>
                {['inconnu','espèces','carte','virement','chèque','Lydia','PayPal','Mollie'].map(m =>
                  <option key={m} value={m}>{m}</option>)}
              </select>
            </div>
          </div>
        </div>

        {/* Lignes produits */}
        <div className={styles.card}>
          <div className={styles.sectionHeader}>
            <h2 className={styles.sectionTitle}>Produits</h2>
            <button className={styles.btnAddLigne} onClick={addLigne}>+ Ajouter un produit</button>
          </div>

          <div className={styles.lignesScroll}>
            <table className={styles.lignesTable}>
              <thead><tr>
                <th>Produit DB</th><th>Nom</th><th>Qté</th>
                <th>PA HT</th><th>PA TTC</th><th>PV HT</th><th>PV TTC</th>
                <th>Remise%</th><th>Total TTC</th><th></th>
              </tr></thead>
              <tbody>
                {lignes.map(l => {
                  const total = n(l.prixVenteTtc) * n(l.quantite) * (1 - n(l.remiseLigne) / 100);
                  return (
                    <tr key={l._key}>
                      <td>
                        <button
                          type="button"
                          className={styles.btnPicker}
                          onClick={() => setPickerKey(l._key)}
                        >
                          {l.productId ? '✓ ' + (l.produitNom || products.find(p => p.id === l.productId)?.name || 'Produit').slice(0, 22) : '🔍 Choisir…'}
                        </button>
                      </td>
                      <td><input className={styles.inputSm} value={l.produitNom}
                        onChange={e => updateLigne(l._key, { produitNom: e.target.value })} placeholder="Nom produit" /></td>
                      <td><input className={`${styles.inputSm} ${styles.inputNum}`} type="number" min="1" value={l.quantite}
                        onChange={e => updateLigne(l._key, { quantite: e.target.value })} /></td>
                      <td><input className={`${styles.inputSm} ${styles.inputNum}`} type="number" step="0.01" value={l.prixAchatHt}
                        onChange={e => updateLigne(l._key, { prixAchatHt: e.target.value, prixAchatTtc: String(Math.round(n(e.target.value) * TVA * 100) / 100) })} /></td>
                      <td><input className={`${styles.inputSm} ${styles.inputNum}`} type="number" step="0.01" value={l.prixAchatTtc}
                        onChange={e => updateLigne(l._key, { prixAchatTtc: e.target.value, prixAchatHt: String(Math.round(n(e.target.value) / TVA * 100) / 100) })} /></td>
                      <td><input className={`${styles.inputSm} ${styles.inputNum}`} type="number" step="0.01" value={l.prixVenteHt}
                        onChange={e => updateLigne(l._key, { prixVenteHt: e.target.value, prixVenteTtc: String(Math.round(n(e.target.value) * TVA * 100) / 100) })} /></td>
                      <td><input className={`${styles.inputSm} ${styles.inputNum}`} type="number" step="0.01" value={l.prixVenteTtc}
                        onChange={e => updateLigne(l._key, { prixVenteTtc: e.target.value, prixVenteHt: String(Math.round(n(e.target.value) / TVA * 100) / 100) })} /></td>
                      <td><input className={`${styles.inputSm} ${styles.inputNum}`} type="number" step="0.1" min="0" max="100" value={l.remiseLigne}
                        onChange={e => updateLigne(l._key, { remiseLigne: e.target.value })} /></td>
                      <td className={`${styles.inputSm} ${styles.bold}`}>{fmt(total)}</td>
                      <td>
                        <div className={styles.lineBtns}>
                          {dirtyLines.has(l._key) && l.productId && (
                            <button className={styles.btnUpdateProd} onClick={() => handleUpdateProduct(l)} title="Enregistrer PA / PV dans la base produit">
                              ↑ Màj produit
                            </button>
                          )}
                          <button className={styles.btnRemoveLigne} onClick={() => removeLigne(l._key)}>✕</button>
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>

        {/* Totaux */}
        <div className={styles.card}>
          <h2 className={styles.sectionTitle}>Récapitulatif</h2>
          <div className={styles.totalsGrid}>
            <div className={styles.field}>
              <label className={styles.label}>Remise générale (%)</label>
              <input className={styles.input} type="number" step="0.1" min="0" max="100" value={form.remiseGenerale}
                onChange={e => setForm(f => ({ ...f, remiseGenerale: e.target.value }))} />
            </div>
            <div className={styles.field}>
              <label className={styles.label}>Frais de port (€)</label>
              <input className={styles.input} type="number" step="0.01" min="0" value={form.fraisPort}
                onChange={e => setForm(f => ({ ...f, fraisPort: e.target.value }))} />
            </div>
          </div>

          <div className={styles.totalsCards}>
            <div className={styles.totalItem}><span>Sous-total TTC</span><strong>{fmt(totals.sousTtc)}</strong></div>
            {n(form.remiseGenerale) > 0 && <div className={styles.totalItem}><span>Remise ({form.remiseGenerale}%)</span><strong className={styles.red}>− {fmt(totals.remiseMontant)}</strong></div>}
            {n(form.fraisPort) > 0 && <div className={styles.totalItem}><span>Frais de port</span><strong>{fmt(n(form.fraisPort))}</strong></div>}
            <div className={`${styles.totalItem} ${styles.totalBig}`}><span>Total TTC</span><strong>{fmt(totals.totalTtc)}</strong></div>
            <div className={styles.totalItem}><span>Total HT</span><strong>{fmt(totals.totalHt)}</strong></div>
            <div className={styles.totalItem}><span>Coût achat HT</span><strong className={styles.muted}>{fmt(totals.coutHt)}</strong></div>
            <div className={styles.totalItem}><span>Bénéfice HT</span><strong className={totals.beneficeHt >= 0 ? styles.green : styles.red}>{fmt(totals.beneficeHt)}</strong></div>
            <div className={`${styles.totalItem} ${totals.rentabilite >= 0 ? styles.statGreen : styles.statRed}`}>
              <span>Rentabilité</span><strong>{fmtPct(totals.rentabilite)}</strong>
            </div>
          </div>
        </div>

        <div className={styles.formActions}>
          <button className={styles.btnSave} onClick={handleSave} disabled={saving}>
            {saving ? 'Enregistrement...' : editingId ? '✓ Enregistrer' : '+ Créer la vente'}
          </button>
          <button className={styles.btnCancel} onClick={() => setView('list')}>Annuler</button>
        </div>
      </div>

      {pickerKey !== null && (
        <ProductPicker
          products={products}
          onSelect={prod => { selectProduct(pickerKey, prod); setPickerKey(null); }}
          onClose={() => setPickerKey(null)}
        />
      )}
    </Layout>
  );
};
